import Immutable from 'immutable';
import {createSelector} from 'reselect';
import {hasNamesAndNumbers, hasUpload} from '~/selectors/CanvasSelectors';
import {VIEW_ID_MAP} from '~/constants/ProductConstants';
import {isSingles} from '~/utils/quote';

const INVENTORY_ZIP_CODE = '43062'; // warehouse in Ohio

export const getQuoteState = state => state.quote?.present || state.quote;

export const hasColorfulQuote = state =>
  getQuoteState(state).get('colorfulQuote');
export const hasFullQuote = state => getQuoteState(state).get('hasFullQuote');
export const isFetchingPricesWithoutDroppedView = state =>
  getQuoteState(state).get('isFetchingPricesWithoutDroppedView');
export const getIsInventoryShippingType = state =>
  getQuoteState(state).get('isInventoryShippingType');
export const hasQuote = state => !!getQuoteState(state).get('totalPrice');
export const hasTouchedQuantities = state =>
  getQuoteState(state).get('touchedQuantities');
export const isPriceStale = state => getQuoteState(state).get('isPriceStale');
export const getBuyMorePrice = state =>
  getQuoteState(state).get('buyMorePrice');
export const getBuyMoreQuantity = state =>
  getQuoteState(state).get('buyMoreQuantity');
export const getBuyMoreUnitPrice = state =>
  getQuoteState(state).get('buyMoreUnitPrice');
export const getCaveats = state => getQuoteState(state).get('caveats')?.toJS();
export const getColorCount = state =>
  getQuoteState(state).get('colorCount')?.toJS();
export const getDecorationMethod = state =>
  getQuoteState(state).get('decorationMethod');
export const getDroppedView = state => getQuoteState(state).get('droppedView');
export const getNcoEligible = state => getQuoteState(state).get('ncoEligible');
export const getPostalCode = state => {
  if (getIsInventoryShippingType(state)) {
    return INVENTORY_ZIP_CODE;
  }
  return getQuoteState(state).get('postalCode');
};
export const getPriceGrid = state => getQuoteState(state).get('priceGrid');
export const getServiceCharges = state =>
  getQuoteState(state)?.get('service_charges')?.toJS();
export const getTotalPrice = state => getQuoteState(state).get('totalPrice');
export const getRealTimeTotalPrice = state =>
  getQuoteState(state).get('realTimeTotalPrice');
export const getVolumeDiscountPercentage = state =>
  getQuoteState(state).get('volumeDiscountPercentage');
export const getVolumeDiscountPrice = state =>
  getQuoteState(state).get('volumeDiscountPrice');
export const getVolumeDiscountSavings = state =>
  getQuoteState(state).get('volumeDiscountSavings');
export const getItemizedPrices = state =>
  getQuoteState(state).get('itemizedPrices');
export const getItemizedPricesWithoutDroppedView = state =>
  getQuoteState(state).get('itemizedPricesWithoutDroppedView');
export const getRealTimeItemizedPrices = state =>
  getQuoteState(state).get('realTimeItemizedPrices');
export const getOneColorLessPrice = state =>
  getQuoteState(state).get('oneColorLessPrice');
export const getOneViewLessPrice = state =>
  getQuoteState(state).get('oneViewLessPrice');
export const getSleeveViewLessPrice = state =>
  getQuoteState(state).get('sleeveViewLessPrice');
export const getSleeveViewLessSaveAmount = state =>
  getQuoteState(state).get('sleeveViewLessSaveAmount');
export const getEmbroideryToPrintPrice = state =>
  getQuoteState(state).get('embroideryToPrintPrice');
export const getSwappedPrintMethod = state =>
  getQuoteState(state).get('swappedPrintMethod');
export const getQuoteBlank = state => getQuoteState(state).get('quoteBlank');
export const getQuantities = state => getQuoteState(state).get('quantities');
export const getQuantitiesJs = state =>
  getQuoteState(state).get('quantities').toJS();
export const getQuoteMode = state => getQuoteState(state).get('mode');
export const getShipping = state => getQuoteState(state).get('shipping');
export const getShowShippingForm = state =>
  getQuoteState(state).get('showShippingForm');
export const getUnavailableCoreSizes = state =>
  getQuoteState(state).get('unavailableCoreSizes');

const sumReducer = (acc, val) => acc + val;

export const getItemizedPricesJs = createSelector(
  [getItemizedPrices],
  itemizedPrices => itemizedPrices.toJS()
);

export const getRealTimeItemizedPricesJs = createSelector(
  [getRealTimeItemizedPrices],
  itemizedPrices => itemizedPrices.toJS()
);

export const hasQuantitiesForAllProducts = createSelector(
  [getQuantities],
  quantities => quantities.every(quantity => quantity.some(size => size > 0))
);

export const getFirstItemWithQuantities = createSelector(
  [getQuantities],
  quantities => quantities.findKey(quantity => quantity.some(size => size > 0))
);

export const getPriceGridJs = createSelector(
  [getPriceGrid],
  priceGrid => priceGrid && priceGrid.toJS()
);

export const getQuoteItems = createSelector(
  [getQuantities, getItemizedPrices],
  (quantities, prices) =>
    quantities.map((item, colorId) => {
      const itemPrices = prices.get(colorId);
      const totalQuantity = item.reduce(
        (acc, key, val) => acc + parseInt(val, 10),
        0
      );
      const quoteItems = Immutable.Map({
        quantities: item,
        totalQuantity
      });
      if (itemPrices) {
        return quoteItems.merge({
          subtotal: itemPrices.get('subtotal'),
          unitPrice: itemPrices.get('itemPrice')
        });
      }
      return quoteItems;
    })
);

export const getQuoteItemsArray = createSelector([getQuoteItems], quoteItems =>
  quoteItems
    .map((item, key) => {
      return {
        ...item,
        productId: key
      };
    })
    .toArray()
);

export const getQuoteItemsJs = createSelector([getQuoteItems], quoteItems =>
  quoteItems.toJS()
);

export const getRushDeliveryJs = createSelector([getShipping], shipping =>
  shipping.has('rushDelivery') ? shipping.get('rushDelivery').toJS() : undefined
);

export const getShippingCaveat = createSelector([getShipping], shipping =>
  shipping.get('shippingCaveat')
);

export const getShippingCity = createSelector([getShipping], shipping =>
  shipping.get('city')
);

export const getStandardDeliveryJs = createSelector([getShipping], shipping =>
  shipping.has('standardDelivery')
    ? shipping.get('standardDelivery').toJS()
    : undefined
);

export const getStandardDeliveryDate = createSelector([getShipping], shipping =>
  shipping.getIn(['standardDelivery', 'deliveryDate'])
);

export const getShippingState = createSelector([getShipping], shipping =>
  shipping.get('state')
);

export const isShippingTurnAdjusted = createSelector(
  [getShipping],
  shipping => (shipping?.get('turnAdjustment') || '').length > 0
);

export const getStandardTurnTime = createSelector([getShipping], shipping =>
  shipping.getIn(['standardDelivery', 'turnTime'])
);

export const getSuperRushDeliveryJs = createSelector([getShipping], shipping =>
  shipping.has('superRushDelivery')
    ? shipping.get('superRushDelivery').toJS()
    : undefined
);

// Return the total number of styles
export const getStyleCount = createSelector([getQuoteItems], quoteItems => {
  const names = quoteItems.map(item => item.styleId);
  const uniqNames = names.reduce((accum, name) => {
    if (accum.indexOf(name) < 0) {
      accum.push(name);
    }
    return accum;
  }, []);
  return uniqNames.size;
});

// Is this a multi-style quote?
export const isMultiStyle = createSelector(
  [getStyleCount],
  styleCount => styleCount > 1
);

export const getTotalQuantity = state => {
  const quantities = getQuantities(state);
  const namesAndNumbers = hasNamesAndNumbers(state);
  if (namesAndNumbers) {
    const firstProduct = quantities.first();
    const firstProductSizes = (firstProduct && firstProduct.toObject()) || {};
    return Object.values(firstProductSizes).reduce(sumReducer, 0);
  }
  return (
    quantities !== null &&
    quantities.reduce((acc, val) => {
      if (val !== null) {
        return acc + val.reduce(sumReducer, 0);
      } else {
        return acc;
      }
    }, 0)
  );
};

export const isSinglesOrderWithUpload = state => {
  return isSingles(getTotalQuantity(state)) && hasUpload(state);
};

// Flags related to the drop one side / drop sleeve view CTAs
export const getHasDroppedView = createSelector(
  [getDroppedView],
  droppedView => {
    return !!droppedView?.id && !!droppedView?.name;
  }
);

export const getHasDroppedSleeveView = createSelector(
  [getDroppedView],
  droppedView => {
    return (
      droppedView?.id === VIEW_ID_MAP.leftSleeve ||
      droppedView?.id === VIEW_ID_MAP.rightSleeve
    );
  }
);

export const getInkMatchCharge = createSelector(
  [getServiceCharges],
  (serviceCharges = []) =>
    serviceCharges.find(charge => charge.type === 'ink_match')
);
